'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Check, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

type PlanKey = 'starter' | 'growth' | 'growth_governance';
type Phase = 'loading' | 'loggedOut' | 'confirmed' | 'timedOut';

const PLAN_NAMES: Record<PlanKey, string> = {
  starter: 'Initial Starter (Course 1)',
  growth: 'Growers Membership',
  growth_governance: 'Growers & Governance Membership',
};

const ROUTE_RANK: Record<string, number> = {
  starter: 1,
  builders: 2,
  growth: 3,
  growth_governance: 4,
};

const POLL_MS = 3000;
const POLL_MAX_MS = 30000;

function parsePlan(value: string | null): PlanKey | null {
  return value === 'starter' || value === 'growth' || value === 'growth_governance' ? value : null;
}

function firstNameFrom(raw: string | null | undefined): string {
  const word = (raw ?? '').trim().split(/\s+/)[0] ?? '';
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : '';
}

export default function WelcomeContent() {
  const searchParams = useSearchParams();
  const plan = parsePlan(searchParams?.get('plan') ?? null);
  const planName = plan ? PLAN_NAMES[plan] : null;
  const planLabel = planName ?? 'your Care Business School membership';

  const [phase, setPhase] = useState<Phase>('loading');
  const [firstName, setFirstName] = useState('');
  const [nextHref, setNextHref] = useState('/dashboard');
  const [runId, setRunId] = useState(0);
  const cancelledRef = useRef(false);

  const returnPath = plan ? `/welcome?plan=${plan}` : '/welcome';

  const routeMatches = useCallback(
    (route: string | null | undefined): boolean => {
      if (!plan) return true;
      if (!route) return false;
      const have = ROUTE_RANK[route] ?? 0;
      return have >= ROUTE_RANK[plan];
    },
    [plan]
  );

  useEffect(() => {
    cancelledRef.current = false;
    const supabase = createClient();
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let started = false;

    const resolveNextHref = async (): Promise<string> => {
      try {
        const { data, error } = await supabase.rpc('my_course_lock_status');
        if (error) return '/dashboard';
        const rows = ((data ?? []) as any[])
          .filter((r) => r.unlocked === true && r.completed !== true)
          .sort((a, b) => Number(a.course_number) - Number(b.course_number));
        const next = rows[0];
        if (!next) return '/dashboard';
        const { data: course } = await supabase
          .from('courses')
          .select('slug')
          .eq('id', next.course_id)
          .maybeSingle();
        return course?.slug ? `/courses/${course.slug}` : '/dashboard';
      } catch {
        return '/dashboard';
      }
    };

    const loadName = async (user: any) => {
      let name = '';
      try {
        const { data } = await supabase.from('user_profiles').select('full_name').eq('id', user.id).maybeSingle();
        name = firstNameFrom(data?.full_name);
      } catch {
        // fall back to metadata
      }
      if (!name) name = firstNameFrom(user?.user_metadata?.first_name) || firstNameFrom(user?.user_metadata?.full_name);
      return name;
    };

    const begin = async (user: any) => {
      if (started || cancelledRef.current) return;
      started = true;
      const startedAt = Date.now();

      const check = async () => {
        if (cancelledRef.current) return;
        let route: string | null = null;
        try {
          const { data } = await supabase.from('member_billing').select('route').eq('user_id', user.id).maybeSingle();
          route = data?.route ?? null;
        } catch {
          route = null;
        }
        if (cancelledRef.current) return;
        if (routeMatches(route)) {
          const [name, href] = await Promise.all([loadName(user), resolveNextHref()]);
          if (cancelledRef.current) return;
          setFirstName(name);
          setNextHref(href);
          setPhase('confirmed');
          return;
        }
        if (Date.now() - startedAt >= POLL_MAX_MS) {
          setPhase('timedOut');
          return;
        }
        pollTimer = setTimeout(check, POLL_MS);
      };

      await check();
    };

    const init = async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelledRef.current) return;
      if (data?.session?.user) {
        await begin(data.session.user);
      }
    };

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelledRef.current) return;
      if (session?.user && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED')) {
        setTimeout(() => begin(session.user), 0);
      }
    });

    // Give the session a moment to restore; if no user appears, show the logged-out view.
    const loggedOutTimer = setTimeout(async () => {
      if (cancelledRef.current || started) return;
      const { data } = await supabase.auth.getSession();
      if (cancelledRef.current || started) return;
      if (!data?.session?.user) setPhase('loggedOut');
    }, 1500);

    init().then(async () => {
      if (cancelledRef.current || started) return;
      const { data } = await supabase.auth.getSession();
      if (!cancelledRef.current && !started && !data?.session?.user) {
        clearTimeout(loggedOutTimer);
        setPhase('loggedOut');
      }
    });

    return () => {
      cancelledRef.current = true;
      if (pollTimer) clearTimeout(pollTimer);
      clearTimeout(loggedOutTimer);
      sub?.subscription?.unsubscribe();
    };
  }, [plan, routeMatches, runId]);

  useEffect(() => {
    if (phase !== 'loggedOut') return;
    try {
      window.sessionStorage.setItem('cbs-return-to', returnPath);
    } catch {
      // storage unavailable
    }
  }, [phase, returnPath]);

  const handleRefresh = () => {
    setPhase('loading');
    setRunId((n) => n + 1);
  };

  return (
    <div className="rounded-2xl border border-[#C6A667]/40 bg-white p-6 text-center shadow-sm sm:p-10">
      <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#C6A667] bg-[#F8F3E9] text-[#C6A667]">
        <Check size={24} strokeWidth={3} aria-hidden="true" />
      </div>

      {phase === 'loading' && (
        <div role="status" aria-live="polite">
          <Loader2 size={24} className="mx-auto animate-spin text-[#C6A667]" aria-hidden="true" />
          <p className="mt-4 text-base font-semibold text-[#581C32]">Setting up your access…</p>
        </div>
      )}

      {phase === 'loggedOut' && (
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#581C32] sm:text-3xl">
            Thank you for joining Care Business School!
          </h1>
          <div className="mx-auto mt-4 h-px w-16 bg-[#C6A667]" />
          <p className="mt-5 text-sm leading-relaxed text-[#581C32]/80 sm:text-base">
            Your payment for the {planLabel} was successful. If you&apos;re new to Care Business School, we&apos;ve sent you
            an email invitation to set up your login. Please check your inbox, including your spam or junk folder. If you
            already have an account, log in to continue.
          </p>
          <Link
            href="/login"
            className="mt-7 inline-flex w-full items-center justify-center rounded-lg bg-[#581C32] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#581C32]/90 sm:w-auto"
          >
            Log in
          </Link>
        </div>
      )}

      {phase === 'confirmed' && (
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#581C32] sm:text-3xl">
            {planName ? `Welcome to the ${planName}${firstName ? `, ${firstName}` : ''}!` : `Welcome${firstName ? `, ${firstName}` : ''}!`}
          </h1>
          <div className="mx-auto mt-4 h-px w-16 bg-[#C6A667]" />
          <p className="mt-5 text-sm leading-relaxed text-[#581C32]/80 sm:text-base">
            Your payment was successful and your access has been updated.
          </p>
          {plan === 'growth_governance' && (
            <p className="mt-3 text-sm leading-relaxed text-[#581C32]/80 sm:text-base">
              Your mentorship sessions will appear in your portal under My mentorship sessions.
            </p>
          )}
          <Link
            href={nextHref}
            className="mt-7 inline-flex w-full items-center justify-center rounded-lg bg-[#581C32] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#581C32]/90 sm:w-auto"
          >
            Continue to your next course
          </Link>
        </div>
      )}

      {phase === 'timedOut' && (
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#581C32] sm:text-3xl">Thank you!</h1>
          <div className="mx-auto mt-4 h-px w-16 bg-[#C6A667]" />
          <p className="mt-5 text-sm leading-relaxed text-[#581C32]/80 sm:text-base">
            Your payment was successful. It can take a few minutes for your access to update. Please refresh this page
            shortly, or contact us if your new courses do not appear within the hour.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex w-full items-center justify-center rounded-lg bg-[#581C32] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#581C32]/90 sm:w-auto"
            >
              Refresh
            </button>
            <Link
              href="/dashboard"
              className="inline-flex w-full items-center justify-center rounded-lg border border-[#581C32] px-6 py-3 text-sm font-semibold text-[#581C32] transition-colors hover:bg-[#581C32]/5 sm:w-auto"
            >
              Go to my dashboard
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
